<template>
  <div class="filter-select">
    <div
      class="filter-select__input-area d-flex justify-content-between align-items-center"
      v-on="eventList"
      data-test-id="filter-select__button"
    >
      <template v-if="labelsSelected.length == 0">
        <span class="filter-select__placeholder">{{ placeholder }}</span>
      </template>
      <template v-else>
        <div
          class="filter-select__label-content"
          v-tooltip.top="formatterTooltip"
        >
          <template
            v-for="(label, index) in labelsSelected.slice(0, 3)"
            :key="index"
          >
            <span class="filter-select__label-item">
              {{ formatString(label) }}
            </span>
          </template>
        </div>
      </template>
      <i class="icon-nav-bullet_down"></i>
    </div>

    <div
      ref="filterSelectOptions"
      class="filter-select__options-list"
      v-if="showSelectOptions"
      @mouseleave="mouseLeaveSelectOptions"
      @mouseenter="mouseEnterSelectOptions"
    >
      <div
        class="filter-select__input-area d-flex justify-content-between align-items-center mb-3"
        @click="openSelect()"
      >
        <span class="filter-select__placeholder-list">FILTRAR POR</span>
        <i class="icon-nav-bullet_up"></i>
      </div>

      <template v-for="(keyName, index) in Object.keys(options)" :key="index">
        <details :id="`details-${index}`" class="filter-select__details-header">
          <summary
            @click="toggleDetails(index)"
            data-test-id="filter-select__summary"
          >
            {{ keyName }}
            <i
              :class="
                isOpen[index] ? 'icon-nav-bullet_up' : 'icon-nav-bullet_down'
              "
            ></i>
          </summary>
          <div class="filter-select__content">
            <template
              v-for="(item, index_item) in options[keyName]"
              :key="index_item"
            >
              <div
                class="d-flex align-items-center justify-content-between filter-select__item"
                data-test-id="filter-select__option"
              >
                <label :for="item.value">{{ item.label }}</label>
                <Checkbox
                  name="options"
                  role="checkbox"
                  :inputId="item.value"
                  v-model="selectedData[keyName]"
                  :value="item"
                  data-test-id="filter-select__checkbox"
                />
              </div>
            </template>
          </div>
        </details>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  nextTick,
  computed,
  watch,
  toRef,
  onMounted,
} from "vue";

type IEventList = {
  click: () => void;
  mouseleave: null | (() => void);
  mouseenter: null | (() => void);
};

export default defineComponent({
  name: "FilterSelect",

  emits: ["changeValue"],

  props: {
    options: {
      type: Object,
      required: true,
    },
    verifyClear: {
      type: Object,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Filtrar por",
    },
  },

  setup(props, { emit }) {
    const isOpen = ref<any>([]);
    const selectedData = toRef({});
    const showSelectOptions = ref<boolean>(false);
    const filterSelectOptions = ref<HTMLElement | null>(null);

    const toggleDetails = (index) => {
      isOpen.value[index] = !isOpen.value[index];
    };

    const formatString = (string: string) => {
      if (string.length > 6) {
        return string.substring(0, 3) + "...";
      }
      return string;
    };

    const openSelect = () => {
      isOpen.value = [];
      window.removeEventListener("click", openSelect, false);

      showSelectOptions.value = !showSelectOptions.value;

      nextTick(() => {
        const openFirstDetails = document.getElementById("details-0");

        if (openFirstDetails) {
          openFirstDetails.setAttribute("open", "");
          isOpen.value[0] = true;
        }
      });

      if (showSelectOptions.value) {
        nextTick(() => {
          checkElementVisibleScreen(filterSelectOptions.value);
        });

        eventList.value.mouseleave = mouseLeaveSelectOptions;
        eventList.value.mouseenter = mouseEnterSelectOptions;
      } else {
        eventList.value.mouseleave = null;
        eventList.value.mouseenter = null;
      }
    };

    const mouseLeaveSelectOptions = () => {
      window.addEventListener("click", openSelect, false);
    };

    const mouseEnterSelectOptions = () => {
      window.removeEventListener("click", openSelect, false);
    };

    const checkElementVisibleScreen = (divElement: HTMLElement | null) => {
      if (divElement) {
        const sizeRange = 30;
        let divElementDementions = divElement.getBoundingClientRect();
        let currentDementions = {
          bottom: divElementDementions.bottom + sizeRange,
          right: divElementDementions.right + sizeRange,
        };
        let fullSizeToGoBackTop = 0;
        let fullSizeToGoBackLeft = 0;

        do {
          fullSizeToGoBackTop +=
            currentDementions.bottom >= window.innerHeight ? sizeRange : 0;
          fullSizeToGoBackLeft +=
            currentDementions.right >= window.innerWidth ? sizeRange : 0;
          divElement.style.transform = `translate(-${fullSizeToGoBackLeft}px, -${fullSizeToGoBackTop}px)`;
          divElementDementions = divElement.getBoundingClientRect();
          currentDementions.bottom = divElementDementions.bottom + sizeRange;
          currentDementions.right = divElementDementions.right + sizeRange;
        } while (
          currentDementions.bottom >= window.innerHeight ||
          currentDementions.right >= window.innerWidth
        );
      }
    };

    const eventList = ref<IEventList>({
      click: openSelect,
      mouseleave: null,
      mouseenter: null,
    });

    const labelsSelected = computed(() => {
      const formatter = Object.keys(selectedData.value).reduce(
        (accumulator, key) => {
          return accumulator.concat(
            selectedData.value[key].map((item) => item.label)
          );
        },
        []
      );

      return formatter;
    });

    const formatterTooltip = computed(() => {
      const selectedLabels = labelsSelected.value;

      if (selectedLabels.length > 1) {
        const lastLabel = selectedLabels[selectedLabels.length - 1];
        const otherLabels = selectedLabels.slice(0, -1).join(", ");
        return otherLabels + " e " + lastLabel;
      } else {
        return selectedLabels[0];
      }
    });

    watch(selectedData.value, (newVal) => {
      emit("changeValue", newVal);
    });

    watch(
      () => props.verifyClear,
      (newObj) => {
        if (Object.keys(newObj).length === 0) {
          selectedData.value = newObj;
        }
      }
    );

    onMounted(() => {
      if (Object.keys(props.verifyClear).length > 0) {
        selectedData.value = props.verifyClear;
      }
    });

    return {
      isOpen,
      eventList,
      openSelect,
      selectedData,
      formatString,
      toggleDetails,
      labelsSelected,
      formatterTooltip,
      showSelectOptions,
      filterSelectOptions,
      mouseLeaveSelectOptions,
      mouseEnterSelectOptions,
    };
  },
});
</script>

<style lang="scss">
.filter-select {
  cursor: pointer;
  border-radius: 3px;
  min-width: toRem(200px);
  height: toRem(42.5px);
  padding: toRem(7px) toRem(10px);
  border: 2px solid var(--insights-color-15);
  position: relative;

  &__placeholder {
    font-style: italic;
    font-size: toRem(16px);
    font-family: "text-medium";
    color: var(--insights-color-16);
  }

  &__label-content {
    max-width: toRem(154px);
  }

  &__label-item {
    border-radius: 5px;
    padding: toRem(5px);
    font-size: toRem(13px);
    font-family: "text-bold";
    margin-right: toRem(5px);
    color: var(--insights-color-2);
    background-color: var(--insights-color-6);
  }

  &__placeholder-list {
    font-size: toRem(13px);
    font-family: "text-bold";
    color: var(--insights-color-16);
  }

  &__input-area {
    > i {
      margin-left: toRem(10px);
      font-size: toRem(12px);
      color: var(--insights-color-15);
    }
  }

  &__options-list {
    top: 40px;
    left: 0;
    z-index: 100;
    position: absolute;
    border-radius: 4px;
    width: toRem(200px);
    padding: 1.1rem 1.3rem;
    font-size: toRem(15px);
    color: var(--insights-color-16);
    background: var(--insights-color-2);
    box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.1);
  }

  &__details-header {
    margin-bottom: toRem(12px);

    > summary {
      list-style: none;
      font-size: toRem(16px);
      font-family: "text-medium";
      color: var(--insights-color-11);

      > i {
        font-size: toRem(9px);
        margin-left: toRem(15px);
      }
    }
  }

  &__content {
    margin-top: toRem(16px);
    margin-bottom: toRem(20px);
  }

  &__item {
    cursor: pointer;
    font-size: toRem(14px);
    margin-bottom: toRem(10px);
    font-family: "text-medium";
    color: var(--insights-color-16);

    &:hover {
      font-size: var(14px);
      color: var(--insights-color-17);
      font-family: "text-semi-bold";
    }

    > label {
      cursor: pointer;
    }

    > .p-checkbox {
      > .p-checkbox-box.p-highlight {
        border-color: var(--insights-color-6);
        background: none;

        > .p-checkbox-icon {
          transition-duration: 0.2s;
          color: var(--insights-color-6);
          font-size: 14px;
        }

        &:hover {
          border-color: var(--insights-color-6);
          background: none;
          color: var(--insights-color-2);
        }
      }

      > .p-checkbox-box {
        &:hover {
          border-color: var(--insights-color-6);
        }
      }

      > .p-checkbox-box.p-focus {
        box-shadow: none;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
